
/**
 * Country-specific configurations for currency and phone formatting.
 */
export const COUNTRY_CONFIGS = {
    'India': {
        locale: 'en-IN',
        currency: 'INR',
        currencySymbol: '₹',
        phoneCode: '+91',
        phoneLength: 10,
        phoneRegex: /^\d{10}$/,
    },
    'United Arab Emirates': {
        locale: 'en-AE',
        currency: 'AED',
        currencySymbol: 'AED',
        phoneCode: '+971',
        phoneLength: 9,
        phoneRegex: /^\d{9}$/,
    },
    'Kuwait': {
        locale: 'en-KW',
        currency: 'KWD',
        currencySymbol: 'KWD',
        phoneCode: '+965',
        phoneLength: 8,
        phoneRegex: /^\d{8}$/,
    },
    'United Kingdom': {
        locale: 'en-GB',
        currency: 'GBP',
        currencySymbol: '£',
        phoneCode: '+44',
        phoneLength: 10,
        phoneRegex: /^\d{10}$/,
    },
    'United States': {
        locale: 'en-US',
        currency: 'USD',
        currencySymbol: '$',
        phoneCode: '+1',
        phoneLength: 10,
        phoneRegex: /^\d{10}$/,
    }
};

const DEFAULT_CONFIG = {
    locale: 'en-US',
    currency: 'USD',
    currencySymbol: '$',
    phoneCode: '',
    phoneLength: null,
    phoneRegex: null,
};

export const getCountryConfig = (countryName) => {
    return COUNTRY_CONFIGS[countryName] || DEFAULT_CONFIG;
};

/**
 * Formats a number as currency based on the country.
 */
export const formatCurrency = (amount, countryName) => {
    const config = getCountryConfig(countryName);
    try {
        return new Intl.NumberFormat(config.locale, {
            style: 'currency',
            currency: config.currency,
            minimumFractionDigits: config.currency === 'KWD' ? 3 : 2,
        }).format(amount || 0);
    } catch (e) {
        return `${config.currencySymbol}${(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
    }
};

/**
 * Formats a phone number based on the country.
 */
export const formatPhone = (phone, countryName) => {
    if (!phone) return '';
    const config = getCountryConfig(countryName);

    // Remove all non-numeric characters
    let cleaned = phone.toString().replace(/\D/g, '');

    // If it starts with the country code (without +), remove it for uniform processing
    const codeNoPlus = config.phoneCode.replace('+', '');
    if (codeNoPlus && cleaned.startsWith(codeNoPlus)) {
        cleaned = cleaned.slice(codeNoPlus.length);
    }

    // Re-apply country code and spacing
    if (countryName === 'India' && cleaned.length === 10) {
        return `${config.phoneCode} ${cleaned.slice(0, 5)} ${cleaned.slice(5)}`;
    }

    if (config.phoneCode) {
        return `${config.phoneCode} ${cleaned}`;
    }

    // Fallback: if no country code, just return as is but maybe formatted a bit
    return phone;
};

/**
 * Validates a phone number based on the country.
 */
export const validatePhone = (phone, countryName) => {
    if (!phone) return false;
    const config = getCountryConfig(countryName);

    let cleaned = phone.toString().replace(/\D/g, '');
    const codeNoPlus = config.phoneCode.replace('+', '');

    if (codeNoPlus && cleaned.startsWith(codeNoPlus)) {
        cleaned = cleaned.slice(codeNoPlus.length);
    }

    if (config.phoneLength) {
        return cleaned.length === config.phoneLength;
    }

    return cleaned.length >= 8; // Basic fallback
};

/**
 * Strips formatting to get raw numeric value for phone.
 */
export const getRawPhone = (phone, countryName) => {
    const config = getCountryConfig(countryName);
    let cleaned = phone.toString().replace(/\D/g, '');
    const codeNoPlus = config.phoneCode.replace('+', '');

    if (codeNoPlus && cleaned.startsWith(codeNoPlus)) {
        cleaned = cleaned.slice(codeNoPlus.length);
    }
    return cleaned;
};
